import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupProductsService } from '../services/groupProductsService';
import { toast } from 'sonner';

interface UseGroupProductsOptions {
  groupId: string;
}

export function useGroupProductsV2({ groupId }: UseGroupProductsOptions) {
  const queryClient = useQueryClient();

  // Query to get products in a group
  const {
    data: groupProducts = [],
    isLoading: isLoadingGroupProducts,
    error,
  } = useQuery({
    queryKey: ['group-products', groupId],
    queryFn: async () => {
      if (!groupId) return [];
      return groupProductsService.getGroupProducts(groupId);
    },
    enabled: !!groupId,
  });

  // Mutation to add products to group
  const addProductsToGroup = useMutation({
    mutationFn: async (productIds: string[]) => {
      await groupProductsService.addProductsToGroup(groupId, productIds);
      toast.success(`Added ${productIds.length} product${productIds.length > 1 ? 's' : ''} to group`);
      return { success: true, productIds };
    },
    onSuccess: () => {
      // Invalidate the group products query to refetch
      queryClient.invalidateQueries({ queryKey: ['group-products', groupId] });
      // Also invalidate the floor plan products to show the new products
      queryClient.invalidateQueries({ queryKey: ['lv-products'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to add products: ${error.message}`);
    },
  });

  // Mutation to remove product from group
  const removeProductFromGroup = useMutation({
    mutationFn: async (productId: string) => {
      await groupProductsService.removeProductFromGroup(groupId, productId);
      toast.success('Product removed from group');
      return { success: true, productId };
    },
    onSuccess: () => {
      // Invalidate the group products query to refetch
      queryClient.invalidateQueries({ queryKey: ['group-products', groupId] });
      // Also invalidate the floor plan products
      queryClient.invalidateQueries({ queryKey: ['lv-products'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove product: ${error.message}`);
    },
  });

  // Mutation to update product name
  const updateProductName = useMutation({
    mutationFn: async ({ productId, name }: { productId: string; name: string }) => {
      await groupProductsService.updateProductName(productId, name);
      toast.success('Product name updated');
      return { success: true, productId, name };
    },
    onSuccess: () => {
      // Invalidate the group products query to refetch
      queryClient.invalidateQueries({ queryKey: ['group-products', groupId] });
      // Also invalidate the floor plan products to update the display
      queryClient.invalidateQueries({ queryKey: ['lv-products'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update product name: ${error.message}`);
    },
  });

  return {
    groupProducts,
    isLoadingGroupProducts,
    error,
    addProductsToGroup: addProductsToGroup.mutateAsync,
    removeProductFromGroup: removeProductFromGroup.mutateAsync,
    updateProductName: updateProductName.mutateAsync,
    isAddingProducts: addProductsToGroup.isPending,
    isRemovingProduct: removeProductFromGroup.isPending,
    isUpdatingName: updateProductName.isPending,
  };
}